import { Card } from "@coherence-design-system/controls";
import { Shimmer, ShimmerElementType } from "@fluentui/react";
import { FluentProvider, OptionOnSelectData, webLightTheme } from "@fluentui/react-components";
import { CardContent } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../../../BasicComponents/DisplayAPIsExceptionAlert";
import { logEvent, logException } from "../../../helper/AppInsightsHelper";
import { IArrayKey } from "../../../interfaces/CarouselProps.interfaces";
import { IProductFamily } from "../../../interfaces/customer-product-v2.interfaces";
import { IPartnerContainerProps } from "../../../interfaces/PartnerView.interfaces";
import { CustomToggleButtonGroup } from "../../../SharedComponents/CustomToggleButtonGroup";
import { useDropdownWithUrlSync } from "../../../SharedComponents/Hooks/useDropdownWithUrlSync";
import { useGetPartnerProductTelemetryDataQuery } from "../../../slices/ExplorePartners.slice";
import { arrSortDescByKeyLength } from "../../../Utils/js-common";
import CustomDropdown from "../../ProductView/Common/CustomDropdown";
import { PartnerProductTelemetryChart } from "./PartnerProductTelemetryChart";
import { PartnerProductTelemetryDailyChart } from "./PartnerProductTelemetryDailyChart";
import ProductTelemetryShimmerLoader from "./PartnerProductTelemetryShimmerLoader";
import WarningBanner from "../../../helper/WarningBanner";

export default function PartnerProductTelemetryView(props: IPartnerContainerProps) {
  const DAILY_FAMILIES = ["Microsoft Purview Consumption", "Microsoft Sentinel", "Security Copilot", "Microsoft Defender for Cloud"];
  const [sortedData, setSortedData] = useState<IProductFamily[]>([]);
  const [searchParams] = useSearchParams();
  const partnerOneID = searchParams.get("partnerOneId");
  const mpnID = searchParams.get("mpnId");
  const applicationUrlParam = searchParams.get("application");

  // Use useDropdownWithUrlSync for Partner Type
  const { selectedValue: selectedType, handleSelection: handleTypeSelection } = useDropdownWithUrlSync({
    validValues: ["CPOR", "PAL"],
    defaultValue: "CPOR",
    paramName: "partnerType"
  });

  let partnerType;
  switch (selectedType) {
    case "CPOR":
      partnerType = "CPOR";
      break;
    case "PAL":
      partnerType = "PAL";
      break;
    default:
      partnerType = "CPOR";
      break;
  }

  const { data, isFetching, error } = useGetPartnerProductTelemetryDataQuery({
    partnerOneID,
    mpnIdentifier: mpnID ?? "",
    partnerType: partnerType
  }) as {
    data?: IProductFamily[];
    isFetching: boolean;
    error: any;
  };

  // Use useDropdownWithUrlSync for Product Family
  const productFamilyOptions = useMemo(() => {
    // If data is available, use its product family names, else fallback to static list
    if (data && Array.isArray(data) && data.length > 0) {
      return Array.from(new Set(data.map((item) => item.name)));
    }
    return ["Microsoft Purview", "Microsoft Purview Consumption", "Microsoft Sentinel", "Security Copilot", "Microsoft Defender for Cloud"];
  }, [data]);

  const productFamilyDefault = useMemo(() => {
    if (productFamilyOptions.includes("Microsoft Purview")) {
      return "Microsoft Purview";
    }
    return productFamilyOptions.length > 0 ? productFamilyOptions[0] : "";
  }, [productFamilyOptions]);

  const { selectedValue: selectedFamily, handleSelection: handleFamilySelection } = useDropdownWithUrlSync({
    validValues: productFamilyOptions,
    defaultValue: productFamilyDefault,
    paramName: "family",
    onValueChange: useCallback(() => {
      // Reset granularity and application when family changes
      updateGranularityParam("");
      updateApplicationParam("");
    }, [])
  });

  useEffect(() => {
    // Ensure selected family is valid, else reset to default
    if (selectedFamily && !productFamilyOptions.includes(selectedFamily)) {
      handleFamilySelection({
        optionText: productFamilyDefault,
        optionValue: productFamilyDefault,
        selectedOptions: [productFamilyDefault]
      });
    }
  }, [selectedFamily, productFamilyOptions, productFamilyDefault, handleFamilySelection]);

  // Use useDropdownWithUrlSync for Data Granularity
  const {
    selectedValue: selectedGranularity,
    handleSelection: handleGranularitySelection,
    updateUrlParam: updateGranularityParam
  } = useDropdownWithUrlSync({
    validValues: ["Daily", "Monthly"],
    defaultValue: "Monthly",
    paramName: "granularity"
  });

  const getDropdownAggregationTypes = (apiResponse: IProductFamily[]) => {
    const types = new Set<string>();
    apiResponse?.forEach((product) => {
      if (DAILY_FAMILIES.includes(product.name)) {
        product.data?.forEach((category) =>
          category.data?.forEach((metric) =>
            metric.data?.forEach((point) => {
              const type = point.aggregationType?.trim();
              if (type && type !== "Weekly") types.add(type);
            })
          )
        );
      }
    });
    return Array.from(types).map((type) => ({ value: type, text: type }));
  };

  const getApplicationsByAggregation = (apiResponse: IProductFamily[], selectedFamily: string) => {
    const apps = new Set<string>();
    const selectedProduct = apiResponse.find((product) => product.name === selectedFamily);

    selectedProduct?.data?.forEach((category) =>
      category.data?.forEach((metric) =>
        metric.data?.forEach((point) => {
          const app = point.application?.trim();
          if (app) apps.add(app);
        })
      )
    );

    return Array.from(apps).map((type) => ({ value: type, text: type }));
  };

  const aggregationTypeOptions = useMemo(() => (data ? getDropdownAggregationTypes(data) : []), [data]);
  const applicationTypeOptions = useMemo(
    () => (data && selectedFamily ? getApplicationsByAggregation(data, selectedFamily) : []),
    [data, selectedFamily]
  );

  // Use useDropdownWithUrlSync for Application - only when applicationTypeOptions has data
  const applicationValidValues = useMemo(() => {
    const baseValues = applicationTypeOptions.length > 0 ? applicationTypeOptions.map((opt) => opt.value) : [];

    // If there's an application URL param and it's not already in the options, add it
    if (applicationUrlParam && !baseValues.includes(applicationUrlParam)) {
      return [...baseValues, applicationUrlParam];
    }

    // If no options available, use AllUp as fallback, but also include URL param if it exists
    if (baseValues.length === 0) {
      const fallbackValues = ["AllUp"];
      if (applicationUrlParam && applicationUrlParam !== "AllUp") {
        fallbackValues.push(applicationUrlParam);
      }
      return fallbackValues;
    }

    return baseValues;
  }, [applicationTypeOptions, applicationUrlParam]);

  const {
    selectedValue: selectedApplication,
    handleSelection: handleApplicationSelection,
    updateUrlParam: updateApplicationParam
  } = useDropdownWithUrlSync({
    validValues: applicationValidValues,
    defaultValue: "AllUp",
    paramName: "application"
  });

  // Set initial defaults for granularity
  useEffect(() => {
    if (aggregationTypeOptions.length > 0 && !selectedGranularity) {
      const defaultGranularity = aggregationTypeOptions[0].value;
      updateGranularityParam(defaultGranularity);
    }
  }, [aggregationTypeOptions, selectedGranularity, updateGranularityParam]);

  // Set initial defaults for application
  useEffect(() => {
    if (data && selectedFamily) {
      const newOptions = getApplicationsByAggregation(data, selectedFamily);

      if (newOptions.length > 0) {
        const allUpOption = newOptions.find((opt) => opt.value === "AllUp");
        const defaultApp = allUpOption ? allUpOption.value : newOptions[0].value;
        if (!selectedApplication || !newOptions.find((opt) => opt.value === selectedApplication)) {
          updateApplicationParam(defaultApp);
        }
      } else {
        updateApplicationParam("");
      }
    }
  }, [selectedFamily, data, selectedApplication, updateApplicationParam]);

  useEffect(() => {
    if (data) {
      setSortedData(
        arrSortDescByKeyLength({
          key: "data",
          data: JSON.parse(JSON.stringify(data))
        } as IArrayKey)
      );
    }

    if (error) {
      logException(
        "Partner Page - Product Telemetry Tab",
        "ProductTelemetryView",
        "useGetPartnerProductTelemetryDataQuery",
        error
      );
    }
  }, [data, isFetching, error]);

  useEffect(() => {
    const customProps = {
      productFamily: selectedFamily,
      partnerOneID: partnerOneID,
      partnerOneName: props.partnerOneName
    };
    logEvent("Product Telemetry", "Partner Page", "ProductTelemetryView", customProps);
  }, [selectedFamily]);

  // Custom handler for family selection via toggle buttons
  const handleFamilyToggleSelection = useCallback(
    (family: string) => {
      // Create a mock OptionOnSelectData object
      const mockSelectionData: OptionOnSelectData = {
        optionText: family,
        optionValue: family,
        selectedOptions: [family]
      };
      handleFamilySelection(mockSelectionData);
    },
    [handleFamilySelection]
  );

  const displayFilters = DAILY_FAMILIES.includes(selectedFamily) ? (
    <FluentProvider theme={webLightTheme}>
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "8px", padding: "0 10px" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <CustomDropdown
            defaultOption={{
              optionText: selectedGranularity,
              optionValue: selectedGranularity,
              selectedOptions: [selectedGranularity || ""]
            }}
            onSelectedOption={handleGranularitySelection}
            data={aggregationTypeOptions}
            styles={{ root: { minWidth: 100 } }}
            label="Data Granularity"
          />
        </div>
        {["Microsoft Purview Consumption"].includes(selectedFamily) && (
          <div style={{ display: "flex", alignItems: "center" }}>
            <CustomDropdown
              defaultOption={{
                optionText: selectedApplication,
                optionValue: selectedApplication,
                selectedOptions: [selectedApplication ?? ""]
              }}
              onSelectedOption={handleApplicationSelection}
              data={applicationTypeOptions}
              styles={{ root: { minWidth: 100 } }}
              label="Application"
            />
          </div>
        )}
      </div>
    </FluentProvider>
  ) : null;

  const productFamilyToggleButtons = (
    <div style={{ margin: "20px" }}>
      <CustomToggleButtonGroup
        items={Array.from(new Set(data?.map(({ name }: any) => name)))}
        selected={selectedFamily}
        setSelected={handleFamilyToggleSelection}
        stackProps={{
          styles: {
            inner: {
              gridRowGap: "5px",
              gridColumnGap: "16px",
              gridTemplateColumns: "repeat(8, auto)"
            }
          }
        }}
      />
    </div>
  );

  const displayViewByMetricSelect = isFetching ? (
    <div style={{ flexBasis: "100%" }}>
      <Shimmer
        shimmerColors={{ shimmer: "var(--shimmer-color)" }}
        styles={{
          root: { paddingTop: 10, paddingBottom: 10, paddingLeft: 10, width: "25%" },
          shimmerWrapper: { ".ms-ShimmerGap-root": { background: "var(--colorBodyBackground1)" } }
        }}
        shimmerElements={[
          { type: ShimmerElementType.line, width: "15%" },
          { type: ShimmerElementType.gap, width: 10 },
          { type: ShimmerElementType.line, width: "15%" }
        ]}
      />
    </div>
  ) : (
    <FluentProvider theme={webLightTheme}>
      <CustomDropdown
        label="Association Type"
        defaultOption={{
          optionText: selectedType,
          optionValue: selectedType,
          selectedOptions: [selectedType || ""]
        }}
        onSelectedOption={handleTypeSelection}
        data={[
          { value: "CPOR", text: "CPOR" },
          { value: "PAL", text: "PAL" }
        ]}
        styles={{ root: { flexBasis: "100%", paddingBottom: 10, paddingLeft: 10 } }}
      />
    </FluentProvider>
  );

  if (!isFetching && error) {
    return <DisplayAPIsException error={error} height={250} />;
  }

  return (
    <>
      {displayViewByMetricSelect}
      {isFetching && <ProductTelemetryShimmerLoader />}

      {!isFetching && !error && sortedData.length === 0 && (
        <div
          style={{
            alignItems: "center",
            display: "flex",
            fontSize: 18,
            fontWeight: 600,
            justifyContent: "center",
            padding: 200
          }}
        >
          {selectedType === "CPOR"
            ? "No CPOR associations for security workloads found for this Partner"
            : "No PAL associations for security services found for this Partner"}
        </div>
      )}

      {!isFetching && sortedData.length > 0 && (
        <div className="p-4">
          <WarningBanner message="Note: This page shows the product usage information for Tenants associated with the Partner through CPOR workloads and services in PAL." />
          {productFamilyToggleButtons}
          {displayFilters}

          <Card>
            <CardContent>
              {sortedData
                .filter((x) => x.name === selectedFamily)
                .map(({ name: pName, data: pData }) => {
                  const chartProps = {
                    granularity: selectedGranularity,
                    application: selectedApplication,
                    selectedFamily,
                    carouseldata: {
                      name: "",
                      items: pData.map(
                        ({ name: kpiName, data: kpiData, kustoLink: kpiKustoLink, isCAT, isTCT, hasEngagedAdmin }) => {
                          const normalizedSelectedApplication = selectedApplication?.trim().toLowerCase();
                          const filteredKpiData = ["Microsoft Purview Consumption"].includes(selectedFamily)
                            ? kpiData
                              .map((kpi) => ({
                                ...kpi,
                                data: kpi.data.filter(
                                  (point) => point.application?.trim().toLowerCase() === normalizedSelectedApplication
                                )
                              }))
                              .filter((kpi) => kpi.data.length > 0) // Remove KPIs with no data
                            : kpiData;

                          return {
                            name: kpiName,
                            data: filteredKpiData,
                            kustoLink: kpiKustoLink || "",
                            isTCT: isTCT,
                            isCAT: isCAT,
                            hasEngagedAdmin: hasEngagedAdmin
                          };
                        }
                      )
                    },
                    partnerOneId: partnerOneID ?? "",
                    mpnId: mpnID ?? ""
                  };
                  return selectedGranularity === "Daily" && DAILY_FAMILIES.includes(selectedFamily) ? (
                    <PartnerProductTelemetryDailyChart
                      key={`daily-${pName}-${selectedApplication}-${selectedGranularity}`}
                      {...chartProps}
                    />
                  ) : (
                    <PartnerProductTelemetryChart
                      key={`monthly-${pName}-${selectedApplication}-${selectedGranularity}`}
                      {...chartProps}
                      disableExploreFeaturesButton={selectedFamily !== "Microsoft Intune"}
                    />
                  );
                })}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
